import { DateTime } from "luxon";

import { graphql } from "lib/generated/app-service-gql";
import { CreditResetCadence } from "lib/types/graphQLEnums";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const contactsExportCreditsQuery = graphql(`
  query contactsExportCredits {
    contactsExportCredits {
      totalCredits
      usedCredits
      remainingCredits
      nextResetAt
      resetCadence
    }
  }
`);

/** When and how often the allowance resets. The two always travel together — see {@link reset}. */
export type ContactsCreditsReset = {
  /** Human-formatted end of the current period, e.g. "1 October 2026". */
  date: string;
  cadence: CreditResetCadence;
};

export type ContactsExportCredits = {
  totalCredits: number;
  usedCredits: number;
  /** Authoritative spendable balance — gate/label on this, not `totalCredits − usedCredits`. */
  remainingCredits: number;
  /**
   * Absent only when the company has credits but no allocation rule: the rule is what supplies the
   * cadence *and* schedules the reset, so the server never has one without the other.
   */
  reset?: ContactsCreditsReset;
  /** False while loading or on error — callers must not credit-gate on an unknown balance. */
  balanceKnown: boolean;
  isLoading: boolean;
};

/**
 * Export-credit balance for the contacts table — the current company's CONTACTS allowance for the
 * active period. Null-safe: the query returns zeros when the company isn't credit-enabled.
 */
export function useContactsExportCredits(): ContactsExportCredits {
  const { data, isError, isLoading } = useGraphQlQuery(
    ["contactsExportCredits"],
    contactsExportCreditsQuery,
    [undefined],
    REACT_QUERY_OPTIONS_NEVER_REFETCH,
  );
  const credits = data?.contactsExportCredits;
  return {
    totalCredits: credits?.totalCredits ?? 0,
    usedCredits: credits?.usedCredits ?? 0,
    remainingCredits: credits?.remainingCredits ?? 0,
    // The one place the two nullable fields are narrowed into a pair, so no consumer has to
    // consider a reset date without a cadence (or the reverse).
    reset:
      credits?.nextResetAt && credits.resetCadence
        ? {
            date: DateTime.fromISO(credits.nextResetAt).toFormat("d LLLL yyyy"),
            cadence: credits.resetCadence,
          }
        : undefined,
    balanceKnown: !!credits && !isError,
    isLoading,
  };
}
